import { useEffect, useState } from 'react';

import draftToHtml from 'draftjs-to-html';
import { convertToRaw, EditorState } from 'draft-js';

import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import classes from './FindingsTab.module.css';
import Avatar from '../../../components/UI/Avatar/Avatar';
import addIcon from '../../../assets/icons/plus.svg';
import {
  editConsultationFinding,
  fetchConsultationFindings,
} from '../../../store/slices/consultationFindings';
import {
  editConsultationRecommendation,
  fetchConsultationRecommendations,
} from '../../../store/slices/consultationRecommendations';
import { useAppDispatch } from '../../../store/store';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import '../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { htmlToEditorState } from '../../../utils/textEditorUtils';
import TextEditor from '../../../components/UI/Inputs/TextEditor';
import { useTranslation } from '../../../hooks/useTranslations';
import { toast } from 'react-toastify';
import { CHARACTER_LIMITS, characterLimitMessage } from '../../../utils/characterLimits';

type Props = {
  type: 'Findings' | 'Recommendations';
  selectedSalesOrder: SalesOrderInfo;
  canEditConsultation?: boolean;
};

const FindingsTab: React.FC<Props> = ({ type, selectedSalesOrder, canEditConsultation }) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const [findingsText, setFindingsText] = useState<string>('');
  const [recommendationsText, setRecommendationsText] = useState<string>('');

  useEffect(() => {
    if (type === 'Findings') setEditorState(htmlToEditorState(findingsText));
    if (type === 'Recommendations') setEditorState(htmlToEditorState(recommendationsText));
  }, [findingsText, recommendationsText, type]);

  useEffect(() => {
    dispatch(fetchConsultationFindings());
    dispatch(fetchConsultationRecommendations());
  }, [dispatch, selectedSalesOrder]);

  useEffect(() => {
    if (type === 'Findings') {
      setFindingsText(
        selectedSalesOrder.consultation_findings?.consultation_findings_description ?? ''
      );
    }
    if (type === 'Recommendations') {
      setRecommendationsText(
        selectedSalesOrder.consultation_recommendations?.consultation_recommendations_description ??
          ''
      );
    }
  }, [selectedSalesOrder, type]);

  const handleSave = () => {
    const limit =
      type === 'Findings' ? CHARACTER_LIMITS.findings : CHARACTER_LIMITS.recommendations;
    if (editorState.getCurrentContent().getPlainText().length > limit) {
      toast.error(characterLimitMessage(translations('error.character_limit'), limit), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    if (type === 'Findings') {
      dispatch(
        editConsultationFinding({
          consultationsFindingDescription: draftToHtml(
            convertToRaw(editorState?.getCurrentContent())
          ),
          consultationFindingId:
            selectedSalesOrder?.consultation_findings?.consultation_findings_id ?? 0,
        })
      );
    } else {
      dispatch(
        editConsultationRecommendation({
          consultationsRecommendationDescription: draftToHtml(
            convertToRaw(editorState?.getCurrentContent())
          ),
          consultationRecommendationId:
            selectedSalesOrder?.consultation_recommendations?.consultation_recommendations_id ?? 0,
        })
      );
    }
  };

  const disabledSaveHandler = (): boolean => {
    if (canEditConsultation === false) return true;
    return draftToHtml(convertToRaw(editorState?.getCurrentContent())) === '';
  };

  const onEditorStateChange = (editorState: EditorState) => {
    setEditorState(editorState);
  };

  return (
    <div className={classes.container}>
      <div className={classes.userInfoContainer}>
        <div className={classes.userAvatar}>
          <Avatar isSquared width={70} height={70} />
        </div>
        <div className={classes.userNameContainer}>
          <div className={classes.serviceName}>{selectedSalesOrder.service.service_name}</div>
          <div className={classes.userName}>
            {`${selectedSalesOrder.customer_person.person_firstname} ${selectedSalesOrder.customer_person.person_lastname}`}
          </div>
        </div>
      </div>
      <div className={classes.headerTitle}>
        <div>
          {type === 'Findings' && translations('findings.patient')}
          {type === 'Recommendations' && translations('detailed.patient')}
        </div>
        <div className={classes.addIcon}>
          <img src={addIcon} alt={'add icon'} />
        </div>
      </div>
      <div className={classes.mainContent}>
        <div className={classes.textAreaContainer}>
          {selectedSalesOrder.work_order_service_consultation && (
            <TextEditor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              readOnly={selectedSalesOrder.work_order?.work_order_status_id == 2}
            />
          )}
        </div>
      </div>
      <PrimaryButtonV2
        text={translations('save')}
        containerStyle={{
          padding: '0 20px 20px 20px',
          width: '100%%',
        }}
        buttonStyle={{
          height: 46,
          width: '100%',
        }}
        disabled={disabledSaveHandler()}
        onClick={handleSave}
      />
    </div>
  );
};

export default FindingsTab;
